import { LoadingOverlay, Modal, Space } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { SmallModal } from '../../../components/SmallModal';
import { Title } from '../../../components/Title';
import { FormButtons } from '../../../components/form/FormButtons';
import { mutate } from '../../../util/api/mutate';
import { useUpsertBarcodeForm } from '../hooks/useUpsertBarcodeForm';
import type { BarcodeDto } from '../types/BarcodeDto';
import { UpsertBarcodeForm } from './UpsertBarcodeForm';

interface EditBarcodeModalProps {
	close: () => void;
	isOpened: boolean;
	updateValueInTable: (b: BarcodeDto) => void;
	skuid: string;
	barcode: BarcodeDto;
}

export function EditBarcodeModal({
	isOpened,
	close,
	updateValueInTable,
	skuid,
	barcode,
}: EditBarcodeModalProps) {
	const { organisation } = useParams();

	const [
		showSuccessModal,
		{ open: openSuccessModal, close: closeSuccessModal },
	] = useDisclosure(false);

	const { form, inputs } = useUpsertBarcodeForm(barcode);

	const completeBarcode = useMemo(() => {
		const barcode = inputs;

		barcode.skuid = skuid;

		return barcode;
	}, [skuid, inputs]);

	const editBarcodeMutation = mutate<BarcodeDto>(
		`${organisation}/barcodes/edit/${skuid}`,
		completeBarcode,
	);

	const resetAndClose = () => {
		form.reset();
		close();
		closeSuccessModal();
	};

	const complete = () => {
		if (editBarcodeMutation.data?.value) {
			updateValueInTable(editBarcodeMutation.data.value);
			form.setInitialValues(editBarcodeMutation.data.value);
		}

		resetAndClose();
	};

	return (
		<>
			<Modal
				opened={isOpened}
				onClose={resetAndClose}
				title={<Title label={'Edit Barcode'} />}
				size={'md'}
				centered
			>
				<LoadingOverlay visible={editBarcodeMutation.isLoading} />
				<SmallModal
					isOpen={showSuccessModal}
					close={complete}
					title={'Barcode Edited!'}
					body={'A barcode has been edited. Click Ok to continue...'}
				/>
				<Space h={6} />
				<form
					onSubmit={form.onSubmit(async () => {
						await editBarcodeMutation.mutateAsync().then((value) => {
							if (value.isValid) {
								openSuccessModal();
							}
						});
					})}
				>
					<UpsertBarcodeForm form={form} mutation={editBarcodeMutation} />
					<FormButtons close={resetAndClose} />
				</form>
			</Modal>
		</>
	);
}
